import type { AudioItem, TimelineItem, VideoItem } from '@/types/timeline'
import { waveformCache, getMonoPeaks } from '../services/waveform-cache'
import { resolveMediaUrl } from '../deps/media-library-resolver'
import { useTimelineItemOverlayStore } from '../stores/timeline-item-overlay-store'
import { useTimelineStore } from '../stores/timeline-store'
import { createLogger } from '@/shared/logging/logger'

export interface SilenceRemovalSettings {
  thresholdDb: number
  minSilenceMs: number
  paddingMs: number
  windowMs: number
}

export interface SilenceRange {
  startFrame: number
  endFrame: number
  durationFrames: number
  startMs: number
  endMs: number
  durationMs: number
}

export type SilenceRangesByItemId = Record<string, SilenceRange[]>

export interface SilencePreviewSummaryItem {
  itemId: string
  rangeCount: number
  durationFrames: number
  durationSec: number
}

export interface SilencePreviewSummary {
  items: SilencePreviewSummaryItem[]
  itemCount: number
  itemsWithSilence: number
  totalRanges: number
  totalDurationFrames: number
  totalDurationSec: number
}

export interface ComputeSilenceRangesParams {
  peaks: Float32Array
  sampleRate: number
  timelineFps: number
  speed?: number
  thresholdDb: number
  minSilenceMs: number
  paddingMs: number
  windowMs?: number
}

export const DEFAULT_SILENCE_REMOVAL_SETTINGS: SilenceRemovalSettings = {
  thresholdDb: -45,
  minSilenceMs: 500,
  paddingMs: 100,
  windowMs: 30,
}

export const SILENCE_REMOVAL_ANALYZING_OVERLAY_ID = 'silence-removal-analyzing'
export const SILENCE_REMOVAL_PREVIEW_OVERLAY_ID = 'silence-removal-preview'

const logger = createLogger('SilenceRemovalPreview')

function isSilenceRemovableItem(item: TimelineItem): item is AudioItem | VideoItem {
  return (item.type === 'audio' || item.type === 'video') && !!item.mediaId
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function formatSilenceOverlayLabel(rangeCount: number, durationSec: number): string {
  if (rangeCount <= 0 || durationSec <= 0) {
    return '未检测到静音'
  }
  return `静音 ${rangeCount} 段 · ${durationSec.toFixed(durationSec >= 10 ? 0 : 1)}s`
}

export function computeSilenceRangesFromMonoPeaks({
  peaks,
  sampleRate,
  timelineFps,
  speed = 1,
  thresholdDb,
  minSilenceMs,
  paddingMs,
  windowMs = DEFAULT_SILENCE_REMOVAL_SETTINGS.windowMs,
}: ComputeSilenceRangesParams): SilenceRange[] {
  if (peaks.length === 0 || sampleRate <= 0 || timelineFps <= 0 || speed <= 0) {
    return []
  }

  const thresholdAmplitude = Math.pow(10, thresholdDb / 20)
  const windowSamples = Math.max(1, Math.round((windowMs / 1000) * sampleRate))
  const minSilenceSamples = Math.max(1, Math.round((minSilenceMs / 1000) * sampleRate))
  const paddingSamples = Math.max(0, Math.round((paddingMs / 1000) * sampleRate))
  const silentRuns: Array<{ start: number; end: number }> = []

  let runStart = -1
  let runEnd = -1

  for (let index = 0; index < peaks.length; index++) {
    let windowPeak = 0
    const windowEnd = Math.min(peaks.length, index + windowSamples)
    for (let cursor = index; cursor < windowEnd; cursor++) {
      windowPeak = Math.max(windowPeak, Math.abs(peaks[cursor] ?? 0))
      if (windowPeak > thresholdAmplitude) {
        break
      }
    }

    if (windowPeak <= thresholdAmplitude) {
      if (runStart === -1) {
        runStart = index
      }
      runEnd = index + 1
      continue
    }

    if (runStart !== -1 && runEnd - runStart >= minSilenceSamples) {
      silentRuns.push({ start: runStart, end: runEnd })
    }
    runStart = -1
    runEnd = -1
  }

  if (runStart !== -1 && runEnd - runStart >= minSilenceSamples) {
    silentRuns.push({ start: runStart, end: runEnd })
  }

  if (silentRuns.length === 0) {
    return []
  }

  const mergedRuns: Array<{ start: number; end: number }> = []
  for (const run of silentRuns) {
    const next = {
      start: Math.max(0, run.start - paddingSamples),
      end: Math.min(peaks.length, run.end + paddingSamples),
    }
    const previous = mergedRuns.at(-1)
    if (previous && next.start <= previous.end) {
      previous.end = Math.max(previous.end, next.end)
      continue
    }
    mergedRuns.push(next)
  }

  return mergedRuns
    .map((run) => {
      const startSec = run.start / sampleRate
      const endSec = run.end / sampleRate
      const startFrame = Math.max(0, Math.floor((startSec * timelineFps) / speed))
      const endFrame = Math.max(startFrame + 1, Math.ceil((endSec * timelineFps) / speed))
      const durationFrames = Math.max(1, endFrame - startFrame)
      return {
        startFrame,
        endFrame,
        durationFrames,
        startMs: Math.round(startSec * 1000),
        endMs: Math.round(endSec * 1000),
        durationMs: Math.max(1, Math.round((endSec - startSec) * 1000)),
      }
    })
    .filter((range) => range.durationFrames > 0)
}

function getItemSourceWindow(item: AudioItem | VideoItem, timelineFps: number): {
  sourceStartSeconds: number
  sourceEndSeconds: number
  speed: number
} {
  const sourceFps = item.sourceFps ?? timelineFps
  const speed = item.speed ?? 1
  const sourceStart = item.sourceStart ?? 0
  const visibleSourceFrames = Math.max(1, Math.round((item.durationInFrames / timelineFps) * sourceFps * speed))
  const boundedSourceEnd = item.sourceEnd ?? sourceStart + visibleSourceFrames

  return {
    sourceStartSeconds: sourceStart / sourceFps,
    sourceEndSeconds: boundedSourceEnd / sourceFps,
    speed,
  }
}

export async function analyzeSilenceForItems(
  itemIds: readonly string[],
  settings: SilenceRemovalSettings,
): Promise<SilenceRangesByItemId> {
  const timeline = useTimelineStore.getState()
  const itemsById = new Map(timeline.items.map((item) => [item.id, item]))
  const results: SilenceRangesByItemId = {}
  const analyzableItems: Array<{ itemId: string; item: AudioItem | VideoItem }> = []

  for (const itemId of itemIds) {
    const item = itemsById.get(itemId)
    if (!item || !isSilenceRemovableItem(item)) {
      results[itemId] = []
      continue
    }

    const mediaId = item.mediaId
    if (!mediaId) {
      results[itemId] = []
      continue
    }

    analyzableItems.push({ itemId, item })
  }

  const settledResults = await Promise.allSettled(
    analyzableItems.map(async ({ itemId, item }) => {
      const mediaId = item.mediaId
      if (!mediaId) {
        return { itemId, ranges: [] as SilenceRange[] }
      }

      const url = await resolveMediaUrl(mediaId)
      const waveform = await waveformCache.getWaveform(mediaId, url)
      const monoPeaks = getMonoPeaks(waveform)
      const { sourceStartSeconds, sourceEndSeconds, speed } = getItemSourceWindow(item, timeline.fps)
      const clipStartSample = clampNumber(
        Math.floor(sourceStartSeconds * waveform.sampleRate),
        0,
        monoPeaks.length,
      )
      const clipEndSample = clampNumber(
        Math.ceil(sourceEndSeconds * waveform.sampleRate),
        clipStartSample,
        monoPeaks.length,
      )

      const clipPeaks = monoPeaks.slice(clipStartSample, clipEndSample)
      const clipRanges = computeSilenceRangesFromMonoPeaks({
        peaks: clipPeaks,
        sampleRate: waveform.sampleRate,
        timelineFps: timeline.fps,
        speed,
        thresholdDb: settings.thresholdDb,
        minSilenceMs: settings.minSilenceMs,
        paddingMs: settings.paddingMs,
        windowMs: settings.windowMs,
      }).map((range) => ({
        ...range,
        startFrame: clampNumber(range.startFrame, 0, item.durationInFrames),
        endFrame: clampNumber(range.endFrame, 0, item.durationInFrames),
      }))

      return {
        itemId,
        ranges: clipRanges.filter((range) => range.endFrame > range.startFrame),
      }
    }),
  )

  let successCount = 0
  for (let index = 0; index < settledResults.length; index++) {
    const settled = settledResults[index]
    const itemId = analyzableItems[index]?.itemId

    if (!settled || !itemId) {
      continue
    }

    if (settled.status === 'fulfilled') {
      successCount += 1
      results[itemId] = settled.value.ranges
      continue
    }

    results[itemId] = []
    logger.warn('Silence detection failed for item', {
      itemId,
      reason: settled.reason,
    })
  }

  if (analyzableItems.length > 0 && successCount === 0) {
    throw new Error('无法读取选中片段的音频波形，请检查素材后重试。')
  }

  return results
}

export function applySilencePreviewOverlays(
  itemIds: readonly string[],
  rangesByItemId: SilenceRangesByItemId,
): SilencePreviewSummary {
  const { upsertOverlay, removeOverlay } = useTimelineItemOverlayStore.getState()

  const items = itemIds.map((itemId) => {
    const ranges = rangesByItemId[itemId] ?? []
    const durationFrames = ranges.reduce((sum, range) => sum + range.durationFrames, 0)
    const durationSec = durationFrames / Math.max(1, useTimelineStore.getState().fps)

    if (ranges.length === 0) {
      upsertOverlay(itemId, {
        id: SILENCE_REMOVAL_PREVIEW_OVERLAY_ID,
        label: formatSilenceOverlayLabel(0, 0),
        tone: 'success',
      })
    } else {
      upsertOverlay(itemId, {
        id: SILENCE_REMOVAL_PREVIEW_OVERLAY_ID,
        label: formatSilenceOverlayLabel(ranges.length, durationSec),
        tone: 'warning',
      })
    }

    removeOverlay(itemId, SILENCE_REMOVAL_ANALYZING_OVERLAY_ID)

    return {
      itemId,
      rangeCount: ranges.length,
      durationFrames,
      durationSec,
    }
  })

  return {
    items,
    itemCount: items.length,
    itemsWithSilence: items.filter((item) => item.rangeCount > 0).length,
    totalRanges: items.reduce((sum, item) => sum + item.rangeCount, 0),
    totalDurationFrames: items.reduce((sum, item) => sum + item.durationFrames, 0),
    totalDurationSec: items.reduce((sum, item) => sum + item.durationSec, 0),
  }
}

export function clearSilenceRemovalOverlays(itemIds: readonly string[]): void {
  const { removeOverlay } = useTimelineItemOverlayStore.getState()

  for (const itemId of itemIds) {
    removeOverlay(itemId, SILENCE_REMOVAL_ANALYZING_OVERLAY_ID)
    removeOverlay(itemId, SILENCE_REMOVAL_PREVIEW_OVERLAY_ID)
  }
}